import { assistInput, createAssist, resetAssist, updateLimit } from '../sim/assist';
import { jumpSpeedLimit } from '../sim/bot';
import { BikeState, createBike, stepBike } from '../sim/bike';
import type { ChunkData } from '../sim/chunkgen';
import { ChunkedTerrain, GroundSave } from '../sim/chunks';
import { BikeConfig, DEFAULT_BIKE } from '../sim/config';
import { InputFrame, NEUTRAL_INPUT } from '../sim/input';
import { Level, Placement } from '../sim/level';
import { LEVELS, LevelId } from '../sim/levels';
import { quatFromAxisAngle, quatMul, vec } from '../sim/math';
import { applyRuts } from '../sim/ruts';
import { TerrainSample } from '../sim/terrain';
import { FrameState } from './frame';

export const DT = 1 / 240;
/** Terrain streaming runs every this many steps (10 Hz). */
export const STREAM_EVERY = 24;
/** Simplified mode re-plans its track speed limit every this many steps (20 Hz). */
const ASSIST_PLAN_EVERY = 12;
/** Steps a respawned bike stands still to settle into its static sag (1 s). */
const SETTLE_STEPS = 240;
/** Bike centre above the ground at rest (same as the sim tests). */
export const restHeight = (cfg: BikeConfig) => cfg.suspensionRest + cfg.wheelRadius - 0.08; // about the static sag

export interface WorldSnapshot { bike: BikeState; time: number; steps: number; lastInput: InputFrame; assist: ReturnType<typeof createAssist>; ground: GroundSave }

/**
 * Level, streamed terrain and bike, stepped one input at a time. Live play, drills and replay playback all step
 * through this, so a recorded input log reproduces the run exactly.
 */
/** `cfg` may be swapped between rides (fleet bike picked in the garage, src/app/session.ts setBike). */
export class GameWorld {
  readonly level: Level;
  readonly terrain: ChunkedTerrain;
  readonly bike: BikeState;
  time = 0;
  steps = 0;
  /** The input the physics last stepped with (after the assists in simplified mode). */
  private readonly lastInput: InputFrame = { ...NEUTRAL_INPUT, frontBrake: 0, rearBrake: 0, foot: false, autoFoot: false, assist: false };
  private readonly assist = createAssist();
  private readonly jumpLimit: (s: number) => number;
  private readonly probe: TerrainSample = { height: 0, normal: { x: 0, y: 1, z: 0 }, surface: 'clay' };
  private readonly current: FrameState = { time: 0, dt: 0, bike: null as unknown as BikeState, input: NEUTRAL_INPUT };

  /** `chunks`: terrain chunks generated elsewhere (a worker, at load: src/sim/prefetch.ts), taken before the spawn. */
  constructor(readonly levelId: LevelId, readonly startName = 'default', public cfg: BikeConfig = DEFAULT_BIKE, readonly dt = DT, chunks: readonly ChunkData[] = []) {
    this.level = new Level(LEVELS[levelId]);
    const start = this.level.starts[startName];
    if (!start) throw new Error(`level ${levelId} has no start ${startName}`);
    this.terrain = new ChunkedTerrain(this.level);
    for (const c of chunks) this.terrain.adopt(c);
    this.jumpLimit = jumpSpeedLimit(this.level, true);
    this.bike = createBike(vec());
    this.respawn(start);
  }

  /**
   * Puts the bike back at rest on a placement (a start, a checkpoint, or the last safe point before a fall): pitched
   * to the ground under its two wheels, both wheels on the surface, then settled into its static sag (a second of
   * standing still, deterministic) so the first ride step starts from rest and never from inside the ground.
   */
  respawn(at: Placement): void {
    this.terrain.update(at.x, at.z);
    const cfg = this.cfg, half = cfg.wheelbase / 2;
    const fx = Math.sin(at.heading), fz = Math.cos(at.heading);
    const hr = this.terrain.heightAt(at.x - fx * half, at.z - fz * half), hf = this.terrain.heightAt(at.x + fx * half, at.z + fz * half);
    const pitch = Math.atan2(hf - hr, cfg.wheelbase); // nose up +
    const q = quatMul(quatFromAxisAngle(vec(0, 1, 0), at.heading), quatFromAxisAngle(vec(1, 0, 0), -pitch));
    resetAssist(this.assist);
    Object.assign(this.bike, createBike(vec(at.x, Math.max(hr, hf) + restHeight(cfg), at.z), q, cfg));
    const b = this.bike;
    for (let i = 0; i < SETTLE_STEPS; i++) {
      stepBike(b, NEUTRAL_INPUT, this.terrain, cfg, this.dt);
      b.position.x = at.x; b.position.z = at.z; b.velocity.x = 0; b.velocity.z = 0;
    }
    b.velocity.y = 0; b.angularVelocity.x = 0; b.angularVelocity.y = 0; b.angularVelocity.z = 0;
    b.airTime = 0;
    b.telemetry.landing = null; b.telemetry.lastLanding = null;
  }

  /**
   * True when both wheels stand on the ground with their suspension inside its travel: no tyre below the surface (the
   * check after every respawn, see tests/game/respawn.test.ts).
   */
  wheelsOnGround(): boolean {
    for (const w of this.bike.wheels) if (!w.contact || w.compression < 0 || w.compression > this.cfg.suspensionTravel) return false;
    return true;
  }

  /**
   * The current frame. One object, updated in place every step (the 240 Hz loop allocates nothing): read it before
   * the next step, copy what must be kept.
   */
  frame(): FrameState {
    const f = this.current;
    this.terrain.sampleInto(this.bike.position.x, this.bike.position.z, this.probe);
    f.time = this.time; f.dt = this.dt; f.bike = this.bike; f.input = this.lastInput; f.surface = this.probe.surface;
    return f;
  }

  /**
   * Everything a step reads and writes (bike, clock, assist plan, last input), copied: a ride paused to film a video
   * (src/app/session.ts) is put back exactly with restore(). The ground (ruts, knocked cones) is a copy-on-write save:
   * release() the snapshot once it is not needed.
   */
  snapshot(): WorldSnapshot {
    const s = structuredClone({ bike: this.bike, time: this.time, steps: this.steps, lastInput: this.lastInput, assist: this.assist });
    return { ...s, ground: this.terrain.saveGround() };
  }

  release(s: WorldSnapshot | null | undefined): void { this.terrain.releaseGround(s?.ground); }

  restore(s: WorldSnapshot): void {
    this.terrain.restoreGround(s.ground);
    const { ground: _g, ...rest } = s;
    const c = structuredClone(rest);
    Object.assign(this.bike, c.bike);
    Object.assign(this.lastInput, c.lastInput);
    Object.assign(this.assist, c.assist);
    this.time = c.time;
    this.steps = c.steps;
    this.terrain.update(this.bike.position.x, this.bike.position.z);
  }

  /** m/s the assist's plan allows on the track ahead (Infinity off a known line, or before the first assisted step). */
  get speedLimit(): number { return this.assist.limit; }
  /** The lean trick the assist is riding now (src/sim/assist.ts TRICK). */
  get trick(): number { return this.assist.trick; }

  step(input: InputFrame): FrameState {
    const l = this.lastInput;
    if (input.assist) {
      if (this.steps % ASSIST_PLAN_EVERY === 0) {
        const p = this.bike.position;
        updateLimit(this.assist, this.level.line ?? null, this.jumpLimit, p.x, p.z, this.bike.telemetry.speed);
      }
      assistInput(this.assist, input, this.bike, l);
    }
    else {
      l.throttle = input.throttle; l.brake = input.brake; l.frontBrake = input.frontBrake; l.rearBrake = input.rearBrake;
      l.steer = input.steer; l.body = input.body; l.standing = input.standing; l.foot = input.foot; l.autoFoot = input.autoFoot; l.assist = input.assist; l.autoPosture = input.autoPosture;
    }
    stepBike(this.bike, l, this.terrain, this.cfg, this.dt);
    applyRuts(this.bike, this.terrain, this.cfg, this.dt);
    this.steps++;
    this.time = this.steps * this.dt;
    if (this.steps % STREAM_EVERY === 0) this.terrain.update(this.bike.position.x, this.bike.position.z);
    return this.frame();
  }
}
